/**
 * What every Rowted Admin page sits inside — `docs/rowted-admin-v2.md` §4.1–4.2.
 *
 * Two things, because every one of the seven screens needs both and neither
 * belongs to any of them: the session, and the frame around it.
 *
 * The frame is `AdminFrame`, which is the office app's frame with a black
 * ADMIN tag where the scope switcher would be. `ConsoleFrame` — the dark bar —
 * is gone: §0.1 says one frame, one word different, and the dark bar took the
 * component library with it, which is how the console ended up with a
 * hand-rolled clients table while the office app had a list engine.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { adminRequest, setAdminCsrfToken } from '../../lib/admin-api.js'
import { AdminFrame, ADMIN_NAV, type AdminNavKey, type AdminUser } from '../../frame/admin-frame.js'

export type { AdminUser }

/**
 * The signed-in admin, or the door.
 *
 * `/sign-in?next=…` rather than a console sign-in of its own (§0.7): there is
 * one door now, and it takes you back to the page you were trying to open.
 */
export function useAdminUser(): { me: AdminUser | null; ready: boolean } {
  const navigate = useNavigate()
  const [me, setMe] = useState<AdminUser | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let live = true
    adminRequest<AdminUser>('/me')
      .then((user) => {
        if (!live) return
        /*
         * Re-arm the CSRF token from the session itself, the way
         * `lib/use-me.ts` has always done on the tenant side. The token lives
         * in `sessionStorage`, which is per tab: without this a second tab had
         * a valid cookie, rendered the frame, and then every POST that loads
         * data was refused.
         */
        if (user.csrfToken) setAdminCsrfToken(user.csrfToken)
        setMe(user)
      })
      .catch(() => navigate(
        `/sign-in?next=${encodeURIComponent(window.location.pathname)}`, { replace: true }))
      .finally(() => { if (live) setReady(true) })
    return () => { live = false }
  }, [navigate])

  return { me, ready }
}

/** Whether this role may do a thing, read from the server's own answer. */
export const can = (me: AdminUser | null, ability: string): boolean =>
  Boolean(me?.abilities.includes(ability))

export interface AdminPageProps {
  me: AdminUser | null
  children: ReactNode
  /** Which rail row is current. Worked out from the URL when it is left out. */
  active?: AdminNavKey | null
  status?: string
}

/**
 * The frame, with navigation and sign-out already wired.
 *
 * `me` may be null while `/me` is in flight, because the alternative is a
 * blank screen and then a frame — a page that assembles itself twice reads as
 * a page that is broken.
 */
export function AdminPage({ me, children, active, status }: AdminPageProps) {
  const navigate = useNavigate()
  const location = useLocation()

  const signOut = useCallback(async () => {
    await adminRequest('/auth/sign-out', { method: 'POST', body: {} }).catch(() => {})
    setAdminCsrfToken(null)
    navigate('/sign-in', { replace: true })
  }, [navigate])

  /*
   * **Switch to <company>** is gone from here, and not because it stopped
   * mattering: it is the Environments section of the account menu now
   * (`docs/identity-and-sign-in.md` §2), which lists every place this address
   * can sign in rather than the one special case. The old handler also set the
   * office cookie on *this* host, which §5.2 refuses at a company's own
   * address — so it was going to break the day a client moved to its
   * subdomain. The handoff is a visit now, and the target mints the session.
   */

  const current = active !== undefined
    ? active
    : ADMIN_NAV.find((item) => location.pathname.startsWith(item.path))?.key ?? null

  return (
    <AdminFrame
      me={me ?? PENDING}
      active={current}
      status={status}
      onNavigate={(path) => navigate(path)}
      onSignOut={() => void signOut()}
    >
      {children}
    </AdminFrame>
  )
}

/*
 * What the header draws for the half-second before `/me` answers.
 *
 * `readonly` on purpose: a frame that guesses at a role should guess at the
 * one that can do least, so nothing is briefly offered and then withdrawn.
 */
const PENDING: AdminUser = {
  id: '', name: '…', email: '', role: 'readonly', abilities: [], steppedUpAt: null,
}

export const Loading = () => (
  <p className="m-0 p-4 text-body text-text-muted">Loading…</p>
)
